import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Sparkles, Building2, ArrowRight, Zap, Clock, ChevronLeft, ChevronRight, Gift } from 'lucide-react';

const PromoSlider = () => {
  const { lang, dir } = useLang();
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const isRTL = dir === 'rtl';

  const slides = [
    {
      key: 'ai-accounting',
      badge: lang === 'ar' ? '🎉 عرض محدود' : '🎉 Limited offer',
      accent: 'emerald',
      icon: Sparkles,
      title: lang === 'ar' ? 'المحاسب الذكي' : 'AI Accounting',
      highlight: lang === 'ar' ? 'مجاناً ١٤ يوم' : '14 days free',
      subtitle: lang === 'ar'
        ? 'حوّل كشف حسابك البنكي إلى دفاتر جاهزة خلال دقائق — بدون بطاقة، بدون التزام.'
        : 'Turn your bank statement into finished books in minutes — no card, no commitment.',
      perks: lang === 'ar'
        ? ['تصنيف AI فوري', 'إقرار VAT بضغطة', 'محاسب ذكي يجيبك بالعربي']
        : ['Instant AI classification', 'One-tap VAT return', 'Ask your books anything'],
      cta: lang === 'ar' ? 'ابدأ التجربة المجانية' : 'Start free trial',
      ctaTo: '/accounting/pricing',
      bg: 'from-blue-50 via-white to-blue-50/30',
      border: 'border-blue-200',
      accentBg: 'bg-blue-600',
      accentText: 'text-blue-700',
      shape: 'blue',
    },
    {
      key: 'business-setup',
      badge: lang === 'ar' ? '🏢 تأسيس شركتك' : '🏢 Set up your company',
      accent: 'gold',
      icon: Building2,
      title: lang === 'ar' ? 'تأسيس أعمال في دبي' : 'Business Setup in Dubai',
      highlight: lang === 'ar' ? 'خلال ٥ أيام عمل' : 'in 5 working days',
      subtitle: lang === 'ar'
        ? 'رخصتك التجارية، فيزا شريك، حساب بنكي وعنوان مسجّل — كل شيء تحت سقف واحد.'
        : 'Trade license, partner visa, bank account and registered address — all under one roof.',
      perks: lang === 'ar'
        ? ['0% ضريبة دخل شخصي', 'ملكية أجنبية 100%', '3 أشهر تسجيل PRO مجاناً']
        : ['0% personal income tax', '100% foreign ownership', '3 months PRO service free'],
      cta: lang === 'ar' ? 'ابدأ الآن' : 'Get started',
      ctaTo: '/SetupWizard',
      bg: 'from-sky-50/70 via-white to-blue-50/40',
      border: 'border-blue-200',
      accentBg: 'bg-blue-700',
      accentText: 'text-blue-800',
      shape: 'blue',
    },
  ];

  const restart = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6500);
  };
  useEffect(() => {
    restart();
    return () => timerRef.current && clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (i) => { setIdx(i); restart(); };
  const prev = () => goTo((idx - 1 + slides.length) % slides.length);
  const next = () => goTo((idx + 1) % slides.length);

  const s = slides[idx];
  const Icon = s.icon;

  return (
    <section
      className="relative w-full pt-20 pb-6 sm:pt-24 sm:pb-8"
      dir={dir}
      onMouseEnter={() => timerRef.current && clearInterval(timerRef.current)}
      onMouseLeave={restart}
      data-testid="promo-slider"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className={`relative overflow-hidden rounded-2xl sm:rounded-3xl border ${s.border} bg-gradient-to-br ${s.bg} transition-all duration-700 ease-out`}>
          {/* Decorative shapes */}
          <div className={`absolute -top-24 ${isRTL ? '-left-24' : '-right-24'} w-72 h-72 rounded-full opacity-30 blur-3xl bg-blue-400`} />
          <div className={`absolute -bottom-32 ${isRTL ? '-right-16' : '-left-16'} w-80 h-80 rounded-full opacity-20 blur-3xl bg-sky-300`} />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />

          <div className="relative grid md:grid-cols-2 gap-8 items-center px-6 sm:px-10 md:px-14 py-8 md:py-10">
            {/* Text side */}
            <div key={`text-${s.key}`} className="promo-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/10 bg-white/70 backdrop-blur mb-4 text-[10px] uppercase tracking-widest font-bold text-[#0a0a0a]" data-testid={`promo-badge-${s.key}`}>
                {s.badge}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[#0a0a0a]">
                {s.title}
              </h2>
              <div className={`mt-3 inline-flex items-center gap-2 ${s.accentText}`}>
                <span className={`inline-block w-8 h-[2px] ${s.accentBg}`} />
                <span className="text-lg sm:text-xl font-bold tracking-tight">{s.highlight}</span>
              </div>
              <p className="mt-4 text-sm sm:text-base text-[#111827]/70 leading-relaxed max-w-lg">{s.subtitle}</p>
              <ul className="mt-4 space-y-1.5">
                {s.perks.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-xs sm:text-sm text-[#111827]/80">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.accentBg}`} />
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to={s.ctaTo} data-testid={`promo-cta-${s.key}`}>
                  <button className={`group inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-bold text-white ${s.accentBg} hover:opacity-90 transition-all shadow-lg shadow-black/5`}>
                    {s.cta}
                    <ArrowRight className={`w-4 h-4 transition-transform ${isRTL ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
                  </button>
                </Link>
                {s.key === 'ai-accounting' && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 font-semibold">
                    <Gift className="w-3.5 h-3.5" /> {lang === 'ar' ? 'بدون بطاقة ائتمان' : 'No credit card required'}
                  </span>
                )}
                {s.key === 'business-setup' && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#0a0a0a] font-semibold">
                    <Clock className="w-3.5 h-3.5" /> {lang === 'ar' ? 'استشارة مجانية اليوم' : 'Free consultation today'}
                  </span>
                )}
              </div>
            </div>

            {/* Visual side */}
            <div key={`visual-${s.key}`} className="hidden md:block relative promo-fade-in-delayed">
              {s.key === 'ai-accounting' ? <VisualAI /> : <VisualBusiness />}
            </div>
          </div>

          {/* Controls */}
          <button
            onClick={prev}
            aria-label="Previous"
            className="absolute top-1/2 -translate-y-1/2 start-2 sm:start-3 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/80 backdrop-blur border border-[#e5e7eb] flex items-center justify-center hover:bg-white transition-all text-[#0a0a0a]"
            data-testid="promo-prev"
          >
            <ChevronLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={next}
            aria-label="Next"
            className="absolute top-1/2 -translate-y-1/2 end-2 sm:end-3 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/80 backdrop-blur border border-[#e5e7eb] flex items-center justify-center hover:bg-white transition-all text-[#0a0a0a]"
            data-testid="promo-next"
          >
            <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-8 bg-blue-600' : 'w-1.5 bg-[#0a0a0a]/20 hover:bg-[#0a0a0a]/40'}`}
                data-testid={`promo-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes promoFade {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .promo-fade-in { animation: promoFade 0.6s ease-out both; }
        .promo-fade-in-delayed { animation: promoFade 0.7s ease-out 0.12s both; }
        @keyframes promoFloat {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .promo-float { animation: promoFloat 4s ease-in-out infinite; }
      `}</style>
    </section>
  );
};

// ---------- Visual illustrations (SVG-lite) ----------

const VisualAI = () => (
  <div className="relative h-56 flex items-center justify-end">
    <div className="promo-float bg-white rounded-xl border border-blue-200 shadow-xl shadow-blue-500/10 p-4 w-64">
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#e5e7eb]">
        <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">Bank statement · Feb</div>
          <div className="text-[10px] text-[#111827]/50">AI classified 47 txns</div>
        </div>
      </div>
      {[
        { label: 'Client · ADCB', amount: '+12,500', tag: 'SALES', color: 'bg-blue-100 text-blue-700' },
        { label: 'Rent · Wio', amount: '-3,800', tag: 'RENT', color: 'bg-rose-100 text-rose-700' },
        { label: 'Salary · payroll', amount: '-5,000', tag: 'SALARY', color: 'bg-amber-100 text-amber-700' },
      ].map((r, i) => (
        <div key={i} className="flex items-center justify-between text-[11px] py-1.5" style={{ animationDelay: `${i * 0.1}s` }}>
          <span className="text-[#0a0a0a]">{r.label}</span>
          <div className="flex items-center gap-1.5">
            <span className={`px-1.5 py-0.5 rounded font-bold text-[9px] tracking-widest ${r.color}`}>{r.tag}</span>
            <span className="font-mono text-[#0a0a0a]">{r.amount}</span>
          </div>
        </div>
      ))}
      <div className="mt-3 pt-3 border-t border-[#e5e7eb] flex items-center justify-between text-[10px]">
        <span className="text-[#111827]/50 uppercase tracking-widest">Net</span>
        <span className="font-mono font-bold text-blue-600">+3,700 AED</span>
      </div>
    </div>
  </div>
);

const VisualBusiness = () => (
  <div className="relative h-56 flex items-center justify-end">
    <div className="promo-float relative bg-white rounded-xl border border-blue-200 shadow-xl shadow-blue-500/10 p-5 w-64">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-blue-700 text-white flex items-center justify-center"><Building2 className="w-4 h-4" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-blue-800 font-bold">Your trade license</div>
          <div className="text-[10px] text-[#111827]/50">Ready in 5 days</div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-sky-50 to-white rounded-lg border border-blue-100 p-3 space-y-2">
        {[
          { k: 'Zone', v: 'IFZA · Dubai' },
          { k: 'Activity', v: 'Consultancy' },
          { k: 'Visas', v: '2 partner' },
          { k: 'Ownership', v: '100% foreign' },
        ].map((r) => (
          <div key={r.k} className="flex items-center justify-between text-[11px]">
            <span className="text-[#111827]/60 uppercase tracking-widest text-[9px]">{r.k}</span>
            <span className="text-[#0a0a0a] font-semibold">{r.v}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        <span className="text-[9px] uppercase tracking-widest text-[#0a0a0a] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700 inline-flex items-center gap-1">
          <Zap className="w-2.5 h-2.5" /> Fast-track
        </span>
      </div>
    </div>
  </div>
);

export default PromoSlider;
